import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import InputGroup from '../../../../library/input-group/input-group';
import Link from '../../../../library/link/link';
import Modal from '../../../../components/modal/modal';
import ProductSwatchSelection from '../../../../components/product-swatch-selection/product-swatch-selection';
import CanonicalFormField from '../canonical-form-field/canonical-form-field';

import { useFitment, useMediaQuery } from '../../../../shared/hooks/hooks';
import useCanonicalDefaultProduct from '../../hooks/use-canonical-default-product/use-canonical-default-product';
import useCanonicalForm from '../../hooks/use-canonical-form/use-canonical-form';

import {
  getNormalizedProductType,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';
import { getCompletedCanonicalFilterSelections } from '../../../../shared/utils/product-detail-utils/product-detail-utils';
import { ProductProp } from '../../../../shared/utils/prop-types/prop-types';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { WHEEL_FILTER_KEYS as FILTER_KEYS } from '../../../../shared/constants/filters';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import './canonical-wheel-form.scss';

const MODAL_TYPES = {
  BOLT_PATTERN: 'BOLT_PATTERN',
  OFFSET: 'OFFSET',
  VPN: 'VPN'
};

function CanonicalWheelForm(props) {
  const {
    isLoading,
    onFilterChange,
    onSelectSwatch,
    product,
    selectedSwatch,
    swatches
  } = props;

  const canonicalForm = useCanonicalForm(product);
  const { shouldIgnoreDefaultProduct } = useCanonicalDefaultProduct(product);
  const [modalType, setModalType] = useState(MODAL_TYPES.BOLT_PATTERN);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedFitment } = useFitment();
  const canonicalFormRef = useRef(canonicalForm);
  const breakpoint = useMediaQuery();

  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);
  const hasSwatches = Boolean(product?.swatches);
  const { filters, queryFilters, selectedFilters, onSelectFilter } =
    canonicalForm;
  const serializedFilters = JSON.stringify(selectedFilters);

  canonicalFormRef.current = canonicalForm;

  function openModal(type) {
    setModalType(type);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
  }

  function getOptions(name) {
    let options = null;

    if (filters[name]) {
      options = filters[name];
    } else if (selectedFilters[name]) {
      options = [selectedFilters[name]];
    }

    return options;
  }

  function getValue(name) {
    let value = null;
    if (selectedFilters[name]) {
      value = selectedFilters[name];
    } else if (
      !isCanonical &&
      productType === PRODUCT_TYPES.WHEELS &&
      product[name]
    ) {
      const productVal = product[name].toString();

      value = { label: productVal, value: productVal };
    }

    return value;
  }

  function onSwatchChange(swatch) {
    if (swatch?.code) {
      onSelectFilter(
        {
          swatchCode: {
            value: swatch.code
          }
        },
        true
      );
      onSelectSwatch?.(swatch);
    }
  }

  function handleSelection(key, value) {
    if (typeof onSelectFilter === 'function' && key && value) {
      onSelectFilter(
        { [key]: { ...value, wasAutoSelected: false } },
        value.shouldResetFilter
      );
    }
  }

  useEffect(() => {
    const isCanonical = isCanonicalProduct(product);
    const productType = getNormalizedProductType(product);
    const { filters, selectedFilters, onComplete, onSelectFilter } =
      canonicalFormRef.current;
    const hasDefaultProduct = Boolean(product?.aggregates?.defaultProduct);
    const hasFilteredBySwatch = !product?.aggregates?.nextFilters?.some(
      f => f.key === FILTER_KEYS.COLOR
    );
    const shouldUseDefaultProduct =
      !isLoading &&
      !shouldIgnoreDefaultProduct &&
      isCanonical &&
      hasDefaultProduct &&
      hasFilteredBySwatch &&
      filters[FILTER_KEYS.RIM_DIAMETER]?.length === 1 &&
      filters[FILTER_KEYS.WIDTH]?.length === 1;

    if (
      (!isCanonical || shouldUseDefaultProduct) &&
      productType === PRODUCT_TYPES.WHEELS
    ) {
      const srcProduct = shouldUseDefaultProduct
        ? product.aggregates.defaultProduct
        : product;
      const { offset, atvOffset, boltPatternVal } = srcProduct || {};
      const wheelRimDiameter = srcProduct?.wheelRimDiameter?.toString();
      const wheelWidth = srcProduct?.wheelWidth?.toFixed(1);

      const completedFilters = getCompletedCanonicalFilterSelections(
        [
          { key: FILTER_KEYS.RIM_DIAMETER, value: wheelRimDiameter },
          { key: FILTER_KEYS.WIDTH, value: wheelWidth },
          { key: FILTER_KEYS.BOLT_PATTERN, value: boltPatternVal },
          { key: FILTER_KEYS.OFFSET, value: offset },
          { key: FILTER_KEYS.ATV_OFFSET, value: atvOffset }
        ],
        selectedFilters
      );
      const completedFilterKeys = Object.keys(completedFilters);

      if (shouldUseDefaultProduct) {
        if (completedFilterKeys.length > 0) {
          const filterKey = completedFilterKeys[0];
          onSelectFilter({ [filterKey]: completedFilters[filterKey] }, false);
        }
      } else {
        onComplete(completedFilters, false);
      }
    }
  }, [product, serializedFilters, isLoading, shouldIgnoreDefaultProduct]);

  useEffect(() => {
    const selectedFiltersKeys = Object.keys(selectedFilters);

    if (
      selectedFiltersKeys.length < 1 &&
      Array.isArray(filters[FILTER_KEYS.COLOR]) &&
      product?.code
    ) {
      let wheelColorSelection;

      if (selectedSwatch) {
        const value = selectedSwatch?.code;
        wheelColorSelection = { [FILTER_KEYS.COLOR]: { label: value, value } };
      }

      if (wheelColorSelection) {
        onSelectFilter(wheelColorSelection);
      }
    } else if (
      selectedFiltersKeys.length > 0 &&
      !selectedFilters[FILTER_KEYS.COLOR] &&
      isArrayWithLength(product?.swatches)
    ) {
      if (product.swatches.find(s => s.code === selectedSwatch?.code)) {
        const value = selectedSwatch?.code;
        onSelectFilter({ [FILTER_KEYS.COLOR]: { label: value, value } });
      } else {
        const newSwatch = product.swatches[0];
        const value = newSwatch?.code;

        onSelectSwatch(newSwatch);
        onSelectFilter({ [FILTER_KEYS.COLOR]: { label: value, value } });
      }
    }
  }, [
    selectedFilters,
    selectedSwatch,
    hasSwatches,
    filters,
    product,
    onSelectFilter,
    onSelectSwatch
  ]);

  useEffect(() => {
    onFilterChange(queryFilters);
  }, [queryFilters, onFilterChange]);

  return (
    <form styleName="container">
      {filters.swatchCode && (
        <div styleName="wheel-swatches">
          <ProductSwatchSelection
            availableSwatches={product?.availableSwatches}
            onSwatchChange={onSwatchChange}
            productCode={product?.code}
            selectedSwatch={selectedSwatch}
            swatches={swatches}
          />
        </div>
      )}

      <InputGroup columnCount={1}>
        {filters[FILTER_KEYS.RIM_DIAMETER]?.length === 1 &&
        filters[FILTER_KEYS.WIDTH]?.length === 1 ? (
          <>
            {!selectedFitment && product && (
              <div>
                <div>Wheel Size:</div>
                <strong>{product?.description?.split(' ')[0]}</strong>
              </div>
            )}
          </>
        ) : (
          <div>
            <span styleName="label">Wheel Size:</span>
            <InputGroup
              columnCount={breakpoint.isMinMd ? 2 : 1}
              label="Wheel Size:"
            >
              <CanonicalFormField
                hideInfoButton
                label="Diameter"
                onChange={value => {
                  handleSelection(FILTER_KEYS.RIM_DIAMETER, value);
                }}
                optionSuffix={'"'}
                options={getOptions(FILTER_KEYS.RIM_DIAMETER)}
                selectedValue={getValue(FILTER_KEYS.RIM_DIAMETER)}
                showSingleOptionValues
              />

              <CanonicalFormField
                hideInfoButton
                label="Width"
                onChange={value => {
                  handleSelection(FILTER_KEYS.WIDTH, value);
                }}
                optionSuffix={'"'}
                options={getOptions(FILTER_KEYS.WIDTH)}
                selectedValue={getValue(FILTER_KEYS.WIDTH)}
                showSingleOptionValues
              />
            </InputGroup>
          </div>
        )}

        <CanonicalFormField
          label="Bolt Pattern"
          onChange={value => {
            handleSelection(FILTER_KEYS.BOLT_PATTERN, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.BOLT_PATTERN)}
          options={getOptions(FILTER_KEYS.BOLT_PATTERN)}
          selectedValue={getValue(FILTER_KEYS.BOLT_PATTERN)}
          showSingleOptionValues={!selectedFitment}
        />

        {!getOptions(FILTER_KEYS.ATV_OFFSET) && (
          <CanonicalFormField
            hasButtonOptions
            label="Offset"
            onChange={value => {
              handleSelection(FILTER_KEYS.OFFSET, value);
            }}
            onOpenModal={() => openModal(MODAL_TYPES.OFFSET)}
            options={getOptions(FILTER_KEYS.OFFSET)}
            selectedValue={getValue(FILTER_KEYS.OFFSET)}
            showSingleOptionValues={!selectedFitment}
          />
        )}

        {getOptions(FILTER_KEYS.ATV_OFFSET) && (
          <CanonicalFormField
            hasButtonOptions
            label="Offset"
            onChange={value => {
              handleSelection(FILTER_KEYS.ATV_OFFSET, value);
            }}
            onOpenModal={() => openModal(MODAL_TYPES.OFFSET)}
            options={getOptions(FILTER_KEYS.ATV_OFFSET)}
            selectedValue={getValue(FILTER_KEYS.ATV_OFFSET)}
            showSingleOptionValues={!selectedFitment}
          />
        )}

        <CanonicalFormField
          label="Vendor Product Number"
          onChange={value => {
            handleSelection(FILTER_KEYS.MANUFACTURER_AID, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.MANUFACTURER_AID)}
          options={getOptions(FILTER_KEYS.MANUFACTURER_AID)}
          selectedValue={getValue(FILTER_KEYS.MANUFACTURER_AID)}
          showSingleOptionValues={!selectedFitment}
        />
      </InputGroup>

      {modalType === MODAL_TYPES.BOLT_PATTERN && (
        <Modal
          heading="What Is A Bolt Pattern?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>BOLT PATTERN</strong>
          <p>
            Bolt patterns are classified using a two number system. The first
            number indicates how many bolt holes are in the wheel and the second
            describes the diameter of the imaginary circle they make. For
            example, a 5-100mm bolt pattern means that the wheel has 5 bolt
            holes equally spaced on a 100mm circle. This distance is measured
            across the wheel center.
            <br />
            <Link target="_blank" to="/learn/bolt-pattern">
              Learn more about Bolt Patterns
            </Link>
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.OFFSET && (
        <Modal
          heading="What Is An Offset?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>OFFSET & BACKSPACING</strong>
          <p>
            The wheel offset measures the distance between the wheel&apos;s
            mounting surface and the centerline of the wheel, while backspacing
            measures the distance between the wheel&apos;s mounting surface and
            inside lip. These measurements help determine which wheels are
            compatible with which vehicles by how much the wheel tucks under the
            fender or pushes the wheel out towards the fender.
            <br />
            <Link target="_blank" to="/learn/offset-backspace">
              Learn more about Offset & Backspacing
            </Link>
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.VPN && (
        <Modal
          heading="What Is Vendor Product Number?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>VENDOR PRODUCT NUMBER</strong>
          <p>
            The vendor product number is the manufacturer&apos;s identification
            system. This number can be used to determine a specific product,
            down to the finest detail.
          </p>
        </Modal>
      )}
    </form>
  );
}

CanonicalWheelForm.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onFilterChange: PropTypes.func,
  onSelectSwatch: PropTypes.func,
  product: ProductProp,
  selectedSwatch: PropTypes.object,
  swatches: PropTypes.func
};

export default CanonicalWheelForm;
